<template>
	<div class="box invoice-supplier-suggestion">
		<h3>{{ $t('invoice.supplier_unknown_title') }}</h3>

		<div class="form-group">
			<label>{{ $t('invoice.supplier_unknown_identifier') }}</label>
			<p class="invoice-supplier-suggestion__hint">{{ $t('invoice.supplier_unknown_identifier_hint') }}</p>

			<div class="input-group">
				<input type="text" class="form-control" v-model="identifier" @keyup.enter="searchPappers">
				<div class="input-group-append">
					<button class="btn btn-secondary" :disabled="searching" @click="searchPappers">
						<font-awesome-icon v-if="searching" :icon="['fal', 'spinner']" spin />
						<font-awesome-icon v-else :icon="['fal', 'search']" />
					</button>
				</div>
			</div>
		</div>

		<div v-if="searched && !pappers" class="alert alert-warning">
			{{ $t('invoice.supplier_unknown_no_result') }}
		</div>

		<div v-if="pappers" class="invoice-supplier-suggestion__card">
			<div class="invoice-supplier-suggestion__label">{{ $t('invoice.supplier_unknown_pappers') }}</div>
			<div class="font-weight-bold">{{ pappers.tiers_rs }}</div>
			<div>{{ pappers.tiers_address1 }}</div>
			<div v-if="pappers.tiers_address2">{{ pappers.tiers_address2 }}</div>
			<div>{{ pappers.tiers_postalcode }} {{ pappers.tiers_town }}</div>
		</div>

		<button v-if="pappers" class="btn btn-primary btn-block mt-3" :disabled="processing" @click="createTiers">
			{{ $t('invoice.supplier_unknown_create') }}
			<font-awesome-icon v-if="processing" :icon="['fal', 'spinner']" spin />
		</button>

		<div class="invoice-supplier-suggestion__separator">{{ $t('global.ou') }}</div>

		<p>{{ $t('invoice.supplier_unknown_existing_hint') }}</p>

		<e-select
			v-model="selected_tiers"
			track-by="tiers_id"
			label="tiers_rs"
			:placeholder="$t('tiers.rechercher')"
			:options="tiers"
			:loading="loading_tiers"
			:searchable="true"
			:allow-empty="true"
			:show-labels="false"
		>
			<template slot="noOptions">{{ $t('global.list_empty') }}</template>
		</e-select>

		<button class="btn btn-primary btn-block mt-3" :disabled="!selected_tiers || processing" @click="linkExisting">
			{{ $t('invoice.supplier_unknown_link') }}
			<font-awesome-icon v-if="processing" :icon="['fal', 'spinner']" spin />
		</button>
	</div>
</template>


<script type="text/javascript">
	import Invoice from "@/mixins/Invoice.js"
	import Tiers from "@/mixins/Tiers.js"

	export default {
		name: "InvoiceSupplierSupplierSuggestion",
		mixins: [Invoice, Tiers],
		props: {
			invoice: { type: Object, default: () => ({}) }
		},

		data() {
			return {
				identifier: '',
				pappers: null,
				searching: false,
				searched: false,
				processing: false,
				loading_tiers: false,
				tiers: [],
				selected_tiers: null
			}
		},

		mounted() {
			this.init_component()
		},

		methods: {
			async init_component() {
				this.identifier = this.detectedIdentifier()

				this.loading_tiers = true
				this.tiers = await this.loadTiers()
				this.loading_tiers = false

				// L'interrogation part d'elle-même quand la facture porte un identifiant : c'est le cas
				// courant, et l'opérateur n'a alors qu'à valider ce qui lui est proposé.
				if(this.identifier) {
					this.searchPappers()
				}
			},

			/**
			 * Identifiant de l'émetteur tel que le document le porte : le SIREN relevé à l'import d'une
			 * facture électronique, ou l'immatriculation reconnue par l'OCR sur un document scanné.
			 *
			 * @return {String}
			 */
			detectedIdentifier() {
				if(this.invoice.suppliersocr_seller_siren) {
					return this.invoice.suppliersocr_seller_siren
				}

				if(!this.invoice.suppliersocr_ocr_data) {
					return ''
				}

				const prediction = JSON.parse(this.invoice.suppliersocr_ocr_data).prediction
				const registrations = prediction ? prediction.supplier_company_registrations || [] : []
				// Le SIRET désigne l'établissement qui facture : plus précis que le SIREN, il est
				// préféré quand l'OCR l'a reconnu.
				const found = registrations.find(r => r.type === 'SIRET') || registrations.find(r => r.type === 'SIREN')

				return found ? found.value : ''
			},

			async searchPappers() {
				if(!this.identifier) {
					return
				}

				this.searching = true
				this.pappers = null
				this.pappers = await this.searchPappersEntreprise(this.identifier)
				this.searched = true
				this.searching = false
			},

			/**
			 * Crée le tiers depuis l'annuaire puis lui rattache la facture.
			 */
			async createTiers() {
				this.processing = true

				try {
					const tiers_id = await this.createTiersFromPappers(this.pappers)

					this.tiers = await this.loadTiers()
					this.selected_tiers = this.tiers.find(tiers => tiers.tiers_id === tiers_id)

					this.processing = false

					if(!this.selected_tiers) {
						this.failureToast('toast.info_save_failed')
						return
					}

					await this.linkExisting()
				}
				catch(error) {
					this.processing = false
					console.error("SupplierSuggestion::createTiers => ERROR", error)
					this.failureToast(typeof error === 'string' ? error : 'toast.info_save_failed')
				}
			},

			/**
			 * Rattache la facture au tiers désigné et mémorise l'identifiant de l'émetteur : les
			 * factures suivantes du même fournisseur se rangeront seules derrière ce tiers, qu'il
			 * s'agisse du fournisseur lui-même ou d'un tiers de rassemblement.
			 */
			async linkExisting() {
				this.processing = true

				const result = await this.linkInvoiceTiers(this.invoice.suppliersocr_id, {
					tiers_id: this.selected_tiers.tiers_id,
					siren: this.pappers ? this.pappers.tiers_siren : this.identifier,
					siret: this.pappers ? this.pappers.tiers_siret : null,
					label: this.pappers ? this.pappers.tiers_rs : null
				})

				this.processing = false

				if(!result) {
					return
				}

				this.successToast('toast.info_modif_succes')

				if(result.propagated_tiers_invoices > 0) {
					this.infoToast('invoice.supplier_tiers_propagated', { nb: result.propagated_tiers_invoices })
				}

				this.$emit('linked', this.selected_tiers.tiers_id)
			}
		}
	}
</script>
